import { useState, useEffect } from "react";
import { Pencil, Trash2, Building2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PhoneLink, EmailLink, AddressLink } from "@/components/marketing/contact-links";

export function LeadDetailDialog({ lead, open, onOpenChange, targets, getSubtypesForTarget, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { setForm(lead ? { ...lead } : null); setEditing(false); }, [lead]);

  if (!lead || !form) return null;

  const target = targets.find((t) => t.id === lead.target_id);
  const subtypes = getSubtypesForTarget(lead.target_id);

  const save = async () => {
    const ok = await onUpdate(lead.id, {
      target_id: form.target_id, subtype_id: form.subtype_id || null,
      business_name: form.business_name, contact_person: form.contact_person,
      phone: form.phone, email: form.email, address: form.address, notes: form.notes,
      is_target: form.is_target, is_event: form.is_event, is_catering: form.is_catering,
    });
    if (ok) setEditing(false);
  };

  const remove = async () => {
    const ok = await onDelete(lead.id);
    if (ok) { setConfirmDelete(false); onOpenChange(false); }
  };

  const toggle = (key) => setForm((f) => ({ ...f, [key]: !f[key] }));

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {editing ? "Edit Contact" : lead.business_name}
            </DialogTitle>
            <DialogDescription>{target?.name}{lead.store_code ? ` · Store ${lead.store_code}` : ""}</DialogDescription>
          </DialogHeader>

          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v, subtype_id: null })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sub-Type</Label>
                  <Select value={form.subtype_id || ""} onValueChange={(v) => setForm({ ...form, subtype_id: v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>{getSubtypesForTarget(form.target_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Business Name</Label>
                <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Contact Person</Label>
                  <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                <div className="space-y-2"><Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Email</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="space-y-2"><Label>Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Notes</Label>
                <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
              <div className="flex gap-2">
                {[["is_target", "Target"], ["is_event", "Event"], ["is_catering", "Catering"]].map(([k, lbl]) => (
                  <Badge key={k} variant={form[k] ? "default" : "outline"} className="cursor-pointer" onClick={() => toggle(k)}>{lbl}</Badge>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setForm({ ...lead }); setEditing(false); }}>Cancel</Button>
                <Button onClick={save} data-testid="save-lead-button">Save</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-1">
                {lead.subtype_id && subtypes.find((s) => s.id === lead.subtype_id) && (
                  <Badge variant="outline">{subtypes.find((s) => s.id === lead.subtype_id).name}</Badge>
                )}
                {lead.is_target && <Badge className="bg-blue-500/15 text-blue-600 border-0">Target</Badge>}
                {lead.is_event && <Badge className="bg-purple-500/15 text-purple-600 border-0">Event</Badge>}
                {lead.is_catering && <Badge className="bg-orange-500/15 text-orange-600 border-0">Catering</Badge>}
              </div>
              <div className="text-sm font-medium">{lead.contact_person}</div>
              <div className="space-y-1.5 text-sm">
                <PhoneLink phone={lead.phone} />
                <EmailLink email={lead.email} />
                <AddressLink address={lead.address} />
              </div>
              {lead.notes && <p className="text-sm text-muted-foreground border-t pt-3">{lead.notes}</p>}
              <DialogFooter className="gap-2">
                <Button variant="outline" className="text-destructive" onClick={() => setConfirmDelete(true)} data-testid="delete-lead-button">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
                <Button onClick={() => setEditing(true)} data-testid="edit-lead-button">
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes {lead.business_name}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-lead" className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
